import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getTagColor, getTagGradient, timeAgo } from '../utils/communityHelpers';
import config from '../config';
import '../styles/PostDetails.css';

/**
 * PostDetailsPage Component
 * Full post detail view matching native app design
 */
const PostDetailsPage = () => {
  const { id, postId } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchPostDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch post details using public endpoint
        const postResponse = await fetch(`${config.API_URL}/groups/${id}/posts/${postId}/public`);
        if (!postResponse.ok) {
          throw new Error('Post not found');
        }
        const postResult = await postResponse.json();
        const postData = postResult.data || postResult;
        setPost(postData);
        
        // Fetch community details
        const communityResponse = await fetch(`${config.API_URL}/groups/${id}/public`);
        if (communityResponse.ok) {
          const communityResult = await communityResponse.json();
          const communityData = communityResult.data || communityResult;
          setCommunity(communityData);
        }
        
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPostDetails();
  }, [id, postId]);
  
  const handleBack = () => {
    navigate(`/community/${id}`);
  };
  
  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTagDisplay = (tag) => {
    if (!tag) return 'Post';
    return tag;
  };

  const getTagBadgeColor = (tag) => {
    switch (tag) {
      case 'Announcement': return '#667eea';
      case 'Mazal Tov': return '#56ab2f';
      case 'Update': return '#4facfe';
      case 'News': return '#ff9a9e';
      case 'Chesed': return '#a8edea';
      case 'Tehilim': return '#fcb69f';
      case 'Event': return '#43e97b';
      case 'Prayer Request': return '#fa709a';
      case 'Community': return '#a8edea';
      case 'Torah': return '#fcb69f';
      default: return '#9bb5ff';
    }
  };
  
  if (loading) {
    return (
      <div className="post-details-loading">
        <div>Loading...</div>
      </div>
    );
  }
  
  if (error || !post) {
    return (
      <div className="post-details-error">
        <div>{error || 'Post not found'}</div>
        <button onClick={handleBack} className="back-to-community-btn">
          Back to Community
        </button>
      </div>
    );
  }
  
  const gradient = getTagGradient(post.tag);
  
  return (
    <div className="post-details-page">
      <div className="post-details-scroll">
        {/* Header with Image or Gradient */}
        <div className="post-header-container">
          {post.image_url && post.image_url.trim() !== '' ? (
            <img 
              src={post.image_url} 
              alt={post.title || 'Post image'}
              className="post-header-image"
            />
          ) : (
            <div 
              className="post-header-gradient"
              style={{
                background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`
              }}
            >
              <div className="post-gradient-text-overlay">
                <div className="post-gradient-main-text">
                  {formatTagDisplay(post.tag)}
                </div>
              </div>
            </div>
          )}

          {/* Back Button */}
          <button className="post-back-button" onClick={handleBack}>
            <ArrowLeft size={16} color="#FFFFFF" />
          </button>
        </div>

        {/* Tag Badge */}
        <div className="post-tag-container">
          <div 
            className="post-tag-badge" 
            style={{ backgroundColor: getTagBadgeColor(post.tag) }}
          >
            <span className="post-tag-text">{post.tag}</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="post-content-container">
          {/* Post Title */}
          <h1 className="post-details-title">
            {post.title || 'Community Post'}
          </h1>

          {/* Date and Time Information */}
          <div className="post-meta-container">
            <div className="post-time-ago">
              {timeAgo(post.created_at)}
            </div>
            <div className="post-full-date">
              {formatFullDate(post.created_at)}
            </div>
          </div>

          {/* Post Content */}
          <div className="post-content-text-container">
            <p className="post-details-content-text">
              {post.content || 'No content available'}
            </p>
            
            {/* Image Caption */}
            {post.image_caption && (
              <p className="post-image-caption-text">
                {post.image_caption}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetailsPage;
